/**
 * The chain-varying facts, one row per environment.
 *
 * ADR-0005: "What varies — the CDP URL path, the USDC contract address, the
 * product wallet, the per-op cap — lives in **one config module**, with no code
 * differences between networks." This file is the committed half of that
 * module: the values that are *facts about a chain* and are therefore the same
 * for anyone who deploys against it. The values that are facts about *our*
 * deployment — the product wallet, the CDP key, the public origin — come from
 * the environment and live in `deployment.ts`.
 *
 * The split is the point. A fact about Base Sepolia cannot be wrong in one
 * deployment and right in another, so committing it removes a way for the two
 * environments to disagree. A fact about our deployment is either a secret or
 * an operational choice, and committing it either leaks or lies.
 *
 * **Two environments, two chains, never both.** Staging is Base Sepolia
 * permanently; production is Base mainnet from M2 (ADR-0004, and the
 * implementation plan's Environments section). Each deployment talks to exactly
 * one chain, which is what let ADR-0004 pick Base-only and sidestep Ponder's
 * one documented non-determinism hazard. There is deliberately no way to
 * express "both" here.
 *
 * **What is NOT here: the EAS constants.** The four schema strings, the four
 * UIDs, the OP-stack predeploys and the ABI fragments are canonical in the
 * public `karvstokk-indexer` repo and mirrored into `packages/shared` under the
 * `constants-drift` CI job (#44). They are also chain-*independent* by ADR-0002,
 * which is the whole reason the cutover is config-only — so they are not
 * chain-varying values and do not belong in a per-chain table. A second copy
 * here would be a second thing for the drift check to miss.
 */

export const ENVIRONMENTS = ["staging", "production"] as const;

export type Environment = (typeof ENVIRONMENTS)[number];

export type Address = `0x${string}`;

export type ChainProfile = {
  readonly environment: Environment;

  /** EIP-155 chain id. */
  readonly chainId: number;

  /** For operator-facing output — startup lines, runbooks. Never user copy. */
  readonly chainName: string;

  /**
   * Circle's native USDC. The product wallet is paid in this token, and the
   * rule-2 sponsorship check reads a balance from it (ADR-0005).
   */
  readonly usdc: Address;

  /**
   * The network segment of the CDP RPC URL —
   * `https://api.developer.coinbase.com/rpc/v1/<here>/<CLIENT_API_KEY>`.
   * This is ADR-0005's "CDP URL path", and it is the entire chain-varying part
   * of the provider configuration: same host, same shape, one word different.
   */
  readonly cdpNetworkPath: string;

  /**
   * Per-op gas cap, or `null` when it has not been measured for this chain.
   *
   * ADR-0005 is explicit that this is "a **measurement, not a decision** —
   * calibrated on Base Sepolia and recorded then, and recalibrated at mainnet
   * (fees differ)". So there is one committed number, for the chain it was
   * measured on, and `null` for the chain where it has not been. A `null` makes
   * the environment variable mandatory rather than optional; see
   * `deployment.ts`.
   */
  readonly defaultPerOpGasCap: number | null;
};

export const CHAIN_PROFILES: Readonly<Record<Environment, ChainProfile>> = {
  staging: {
    environment: "staging",
    chainId: 84532,
    chainName: "Base Sepolia",
    usdc: "0x036CbD53842c5426634e7929541eC2318f3dCF7e",
    cdpNetworkPath: "base-sepolia",

    // ≈3.34M — ADR-0005's ≈4× the worst legitimate op, where the worst
    // legitimate op is deploy + genesis attest + signature attest in one batch,
    // measured at **834,908 gas against a passkey owner** on #66 (see
    // `docs/research/aa-infra.md` § Addendum 2026-09-16). Rounded up to the
    // nearest 10k, as #28's figure was.
    //
    // This replaces #28's 3.14M, which came from 784.5k against a *secp256k1*
    // owner and was always labelled a lower bound. It was: P256/WebAuthn
    // verification costs **+28,137 gas per op**, and a passkey account's
    // deployment a further +22,271 one-off, because a 64-byte public key needs
    // a storage slot a 20-byte address does not.
    //
    // 3.14M was therefore ≈200k *under* 4× the real worst op — which is the
    // direction that refuses legitimate operations rather than the direction
    // that leaks money, and would have surfaced as provider flakiness.
    //
    // It also still covers the Pimlico figure, which ran 0.6-1.1% higher on
    // provider overhead, and the 7702-delegated shape Coinbase Wallet now
    // issues, whose worst op has no deployment in it at all (≈640k, so ≈2.56M
    // at 4×). The larger of the candidate architectures is the safe one to
    // commit while #82 settles which we are actually sponsoring.
    defaultPerOpGasCap: 3_340_000,
  },

  production: {
    environment: "production",
    chainId: 8453,
    chainName: "Base mainnet",
    usdc: "0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913",
    cdpNetworkPath: "base",

    // Deliberately unmeasured, so production refuses to start without a number
    // someone chose.
    //
    // Copying staging's 3.34M here would be the single most tempting wrong
    // thing this file could do, and it is still wrong twice over even now that
    // the passkey figure exists (#66). Mainnet fees differ from Sepolia's; and
    // *which* op is the worst legitimate one is open on mainnet, because
    // Coinbase Wallet now issues 7702-delegated EOAs whose UserOps carry no
    // deployment and whose owner is not a passkey (#82). A cap copied from
    // staging would be sized for an architecture mainnet may not use.
    //
    // The failure mode a default would create is quiet: a cap set too low
    // refuses legitimate operations that look like provider flakiness, and a
    // cap set too high is the unbounded path ADR-0005's caps exist to close.
    defaultPerOpGasCap: null,
  },
};

export function isEnvironment(value: unknown): value is Environment {
  return (
    typeof value === "string" &&
    (ENVIRONMENTS as readonly string[]).includes(value)
  );
}
